/**
 * Classifiche giocatori (V33), la scheda «Giocatori». Contano le sole
 * partite concluse. Gli autogol hanno la loro voce e non si sommano mai ai
 * gol; la media voto non c'e per chi non legge i voti, perche non arriva.
 *
 * In alto i primi cinque per gol, assist, minuti e media voto (per chi non
 * legge i voti, presenze), in card affiancate. Sotto la classifica intera:
 * una riga per giocatore con la maglia, presenze e da titolare, i numeri
 * diversi da zero con l'icona dei loro eventi e a destra, in un tassello, il
 * numero per cui l'elenco e ordinato. La tendina «Ordina per» sta accanto al
 * filtro della competizione.
 */

import { type ReactNode, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ClockIcon, ListOrderedIcon, ShirtIcon, StarIcon, type LucideIcon } from 'lucide-react';
import type { TipoEvento } from '@calcio/shared';
import { Badge } from '@/components/ui/badge';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { cn } from '@/lib/utils';
import { StatoCaricamento } from '../comuni/Testata.js';
import { Filtro } from '../comuni/FiltriVista.js';
import { useDati } from '../comuni/dati.js';
import { coloreVoto } from '../giocatori/colori-stato.js';
import { IconaEvento } from '../partite/eventi.js';
import { PaginaStatistiche, RigaGiocatore, Sezione, Tassello, Voce } from './PaginaStatistiche.js';
import { Maglia } from '../comuni/Maglia.js';
import {
  type ClassificheGiocatori as Dati,
  competizioneDaSearch,
  queryCompetizione,
  type RigaClassifica,
  numero,
  ordina,
} from './statistiche.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

type Colonna = keyof Pick<
  RigaClassifica,
  | 'presenze'
  | 'minuti'
  | 'gol'
  | 'autogol'
  | 'assist'
  | 'tiriSpecchio'
  | 'tiriFuori'
  | 'passaggiCompletati'
  | 'passaggiSbagliati'
  | 'palleConquistate'
  | 'pallePerse'
  | 'gialli'
  | 'rossi'
  | 'votoMedio'
>;

/** Presenze, minuti e voto non sono eventi: hanno un'icona loro, neutra. */
const NEUTRA = 'size-4 shrink-0 text-evento-neutro';

const neutra = (Icona: LucideIcon, className = NEUTRA) => (
  <Icona aria-hidden className={className} />
);

/** Ogni numero della riga: etichetta e icona, quella dei suoi eventi quando e un evento. */
const VOCI: { chiave: Colonna; etichetta: string; icona: (className?: string) => ReactNode }[] = [
  { chiave: 'minuti', etichetta: 'Minuti', icona: (c) => neutra(ClockIcon, c) },
  { chiave: 'presenze', etichetta: 'Presenze', icona: (c) => neutra(ShirtIcon, c) },
  ...(
    [
      ['gol', 'Gol', 'gol'],
      ['assist', 'Assist', 'assist'],
      ['autogol', 'Autogol', 'autogol'],
      ['tiriSpecchio', 'Tiri in porta', 'tiro_specchio'],
      ['tiriFuori', 'Tiri fuori', 'tiro_fuori'],
      ['passaggiCompletati', 'Passaggi riusciti', 'passaggio_completato'],
      ['passaggiSbagliati', 'Passaggi sbagliati', 'passaggio_sbagliato'],
      ['palleConquistate', 'Palle recuperate', 'palla_conquistata'],
      ['pallePerse', 'Palle perse', 'palla_persa'],
      ['gialli', 'Gialli', 'cartellino_giallo'],
      ['rossi', 'Rossi', 'cartellino_rosso'],
    ] as const satisfies readonly (readonly [Colonna, string, TipoEvento])[]
  ).map(([chiave, etichetta, tipo]) => ({
    chiave,
    etichetta,
    icona: (c?: string) => <IconaEvento tipo={tipo} className={c} />,
  })),
];

const VOCE_VOTO = {
  chiave: 'votoMedio' as const,
  etichetta: 'Media voto',
  icona: (c?: string) => neutra(StarIcon, c),
};

/** La media voto nel suo badge colorato per fascia, come nella rosa. */
function Voto({ valore, className }: { valore: number | null | undefined; className?: string }) {
  if (valore == null) return <>—</>;
  return (
    <Badge className={cn('px-1.5 tabular-nums', coloreVoto(valore), className)}>
      {valore.toLocaleString('it-IT')}
    </Badge>
  );
}

export function ClassificheGiocatori() {
  const { ss } = useParams<{ ss: string }>();
  const { search } = useLocation();
  const { dati, errore } = useDati<Dati>(
    `/squadre-stagione/${ss}/statistiche/giocatori${queryCompetizione(competizioneDaSearch(search))}`,
  );
  const [colonna, setColonna] = useState<Colonna | 'giocatore'>('minuti');

  const conVoti = dati?.giocatori.some((g) => 'votoMedio' in g) ?? false;
  const voci = conVoti ? [...VOCI, VOCE_VOTO] : VOCI;
  const nessunaPresenza = dati?.giocatori.every((g) => g.presenze === 0) ?? true;

  const ordinaPer = (
    <Filtro
      etichetta="Ordina per"
      valore={colonna}
      onCambia={(v) => setColonna(v as Colonna | 'giocatore')}
      opzioni={[
        { valore: 'giocatore', etichetta: 'Cognome' },
        ...voci.map((v) => ({ valore: v.chiave, etichetta: v.etichetta })),
      ]}
    />
  );

  return (
    <PaginaStatistiche
      ss={ss!}
      scheda="giocatori"
      competizioni={dati?.competizioni ?? []}
      filtri={dati && !nessunaPresenza && ordinaPer}
    >
      {!dati ? (
        <StatoCaricamento errore={errore} />
      ) : dati.giocatori.length === 0 || nessunaPresenza ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessuna partita conclusa</EmptyTitle>
            <EmptyDescription>
              Presenze, minuti e gol si calcolano dalle partite concluse.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Contenuto giocatori={dati.giocatori} conVoti={conVoti} voci={voci} colonna={colonna} />
      )}
    </PaginaStatistiche>
  );
}

function Contenuto({
  giocatori,
  conVoti,
  voci,
  colonna,
}: {
  giocatori: RigaClassifica[];
  conVoti: boolean;
  voci: typeof VOCI;
  colonna: Colonna | 'giocatore';
}) {
  const podi: { chiave: Colonna; titolo: string; Icona: ReactNode }[] = [
    { chiave: 'gol', titolo: 'Marcatori', Icona: <IconaEvento tipo="gol" className="size-5" /> },
    { chiave: 'assist', titolo: 'Assist', Icona: <IconaEvento tipo="assist" className="size-5" /> },
    { chiave: 'minuti', titolo: 'Minuti', Icona: neutra(ClockIcon, 'size-5 text-evento-neutro') },
    conVoti
      ? {
          chiave: 'votoMedio',
          titolo: 'Media voto',
          Icona: neutra(StarIcon, 'size-5 text-evento-neutro'),
        }
      : {
          chiave: 'presenze',
          titolo: 'Presenze',
          Icona: neutra(ShirtIcon, 'size-5 text-evento-neutro'),
        },
  ];

  // Il cognome parte dalla A, i numeri dal piu alto.
  const ordinati = ordina(giocatori, {
    colonna,
    verso: colonna === 'giocatore' ? 'asc' : 'desc',
  });
  const scelta = voci.find((v) => v.chiave === colonna) ?? voci[0]!;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 @3xl:grid-cols-2 @7xl:grid-cols-4">
        {podi.map((p) => (
          <Podio key={p.chiave} {...p} giocatori={giocatori} />
        ))}
      </div>

      <Sezione
        titolo="Classifica"
        icona={<ListOrderedIcon aria-hidden className="size-5" />}
        nota="Gli autogol non si sommano ai gol. I minuti sono quelli giocati, recuperi compresi."
      >
        <ul className="grid gap-2 @5xl:grid-cols-2">
          {ordinati.map((g, i) => (
            <li key={g.id}>
              <RigaGiocatore id={g.id}>
                <span className="hidden w-6 shrink-0 text-right @md:block text-sm font-semibold text-muted-foreground tabular-nums">
                  {i + 1}
                </span>
                <Maglia ruolo={g.ruolo} numero={g.numero} />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-base font-semibold break-words">
                    {g.cognome} {g.nome}
                  </span>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {g.presenze === 1 ? '1 presenza' : `${g.presenze} presenze`}
                    {g.presenze > 0 && ` · ${g.daTitolare} da titolare`}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    {voci
                      .filter((v) => v.chiave !== 'presenze' && v.chiave !== colonna)
                      .filter((v) => v.chiave === 'minuti' || (g[v.chiave] ?? 0) !== 0)
                      .map((v) => (
                        <Voce
                          key={v.chiave}
                          titolo={v.etichetta}
                          icona={v.icona()}
                          valore={
                            v.chiave === 'votoMedio' ? (
                              <Voto valore={g.votoMedio} />
                            ) : (
                              numero(g[v.chiave])
                            )
                          }
                        />
                      ))}
                  </span>
                </span>
                {colonna !== 'giocatore' && (
                  <Tassello
                    titolo={scelta.etichetta}
                    icona={scelta.icona('size-3.5')}
                    colore={
                      colonna === 'votoMedio' && g.votoMedio != null
                        ? coloreVoto(g.votoMedio)
                        : undefined
                    }
                    valore={numero(g[colonna])}
                  />
                )}
              </RigaGiocatore>
            </li>
          ))}
        </ul>
      </Sezione>
    </div>
  );
}

/** I primi cinque per un numero, senza chi e a zero o non ha voti. */
function Podio({
  chiave,
  titolo,
  Icona,
  giocatori,
}: {
  chiave: Colonna;
  titolo: string;
  Icona: ReactNode;
  giocatori: RigaClassifica[];
}) {
  const primi = ordina(giocatori, { colonna: chiave, verso: 'desc' })
    .filter((g) => (g[chiave] ?? 0) > 0)
    .slice(0, 5);
  return (
    <Sezione titolo={titolo} icona={Icona}>
      {primi.length === 0 ? (
        <StatoVuoto titolo="Ancora nessuno" />
      ) : (
        <ol className="flex flex-col gap-1.5">
          {primi.map((g, i) => (
            <li key={g.id}>
              <Link
                to={`/giocatori/${g.id}`}
                className="flex items-center gap-2 rounded-lg border bg-card px-2.5 py-2 text-sm"
              >
                <span
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums',
                    i === 0
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{g.cognome}</span> {g.nome}
                </span>
                <span className="font-semibold tabular-nums">
                  {chiave === 'votoMedio' ? <Voto valore={g.votoMedio} /> : numero(g[chiave])}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </Sezione>
  );
}
